import { CommonModule } from '@angular/common';
import { Component, forwardRef, HostBinding, Injector, Input, OnInit, ViewChild } from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IMediaFile } from '@smarttypes/core';
import { ensureQuillMentionRegistered } from 'angular-v2-utils';
import { isArray } from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { ToastrService } from 'ngx-toastr';
import { Op } from 'quill/core';
import { Mention } from 'quill-mention';

import { FileLibraryComponent } from '../file-library';

ensureQuillMentionRegistered();

/** Item for the # mention dropdown; `value` is what gets inserted (without #). */
export interface WysiwygMentionItem {
  id: string;
  value: string;
  description?: string;
}

@Component({
  selector: 'ui-wysiwyg-editor',
  templateUrl: './wysiwyg-editor.component.html',
  styleUrls: ['./wysiwyg-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WysiwygEditorComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [TranslateModule, CommonModule, FormsModule, QuillEditorComponent, ReactiveFormsModule],
})
export class WysiwygEditorComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() charLimit = 0;
  @Input() templateTop?: string | undefined;
  @Input() templateBottom?: string | undefined;
  @Input() mentions: string[] = [];
  /** When set, used instead of {@link mentions} for the dropdown (supports per-item description). */
  @Input() mentionItems: WysiwygMentionItem[] = [];
  @Input() mode: 'whatsApp' | 'variables' | 'default' | 'plain' = 'default';
  @Input() toolbar: string[] | string[][] = [];
  @Input() formControl!: FormControl;
  @Input() formControlName!: string;
  @ViewChild(QuillEditorComponent, { static: false })
  editor?: QuillEditorComponent;
  @ViewChild(FormControlDirective, { static: true })
  formControlDirective!: FormControlDirective;
  editorConfig!: QuillModules;
  loaded = false;
  value = '';
  readonly allowedFormats: string[] = [
    'bold',
    'italic',
    'underline',
    'link',
    'header',
    'list',
    'align',
    'direction',
    'image',
    'mention',
  ];

  readonly #mentionDenotationChar = '#';

  constructor(
    private readonly injector: Injector,
    private readonly modalService: BsModalService,
    private readonly toastrService: ToastrService,
    private readonly translateService: TranslateService,
  ) {}

  @HostBinding('class.has-placeholders') get placeholders(): boolean {
    return !!this.templateBottom && !!this.templateTop && !this.readonly;
  }

  get control() {
    return this.formControl || this.controlContainer.control?.get(this.formControlName);
  }

  get controlContainer() {
    return this.injector.get(ControlContainer);
  }

  ngOnInit() {
    if (this.mentions.length > 0 || this.mentionItems.length > 0) {
      this.editorConfig = this.#quillMentionConfig();
    } else {
      this.editorConfig = {
        toolbar: {
          container: this.#setupToolbar(),
          handlers: {
            library: () => {
              this.#openMediaLibrary();
            },
          },
        },
      };
    }
    this.loaded = true;
  }

  registerOnTouched(fn: unknown): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: unknown): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(msg: string): void {
    this.value = msg;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective?.valueAccessor?.setDisabledState?.(isDisabled);
    this.disabled = isDisabled;
  }

  onDrop() {
    this.#removeInlineImagesFromContent();
  }

  onPaste() {
    this.#removeInlineImagesFromContent();
  }

  textChanged(quill: any) {
    if (this.charLimit > 0 && quill.editor.getLength() >= this.charLimit) {
      quill.editor.deleteText(this.charLimit, quill.editor.getLength());
    }
  }

  #openMediaLibrary() {
    const selectionIndex = this.editor?.quillEditor?.getSelection()?.index ?? 0;
    this.modalService
      .show(FileLibraryComponent, {
        class: 'modal-file-library',
        initialState: {
          library: true,
          acceptFiles: ['image/jpg', 'image/jpeg', 'image/png'],
        },
      })
      ?.content?.action?.subscribe((file: IMediaFile) => {
        const imageUrl = file.url?.startsWith('https://') ? file.url : `https://${file.url}`;
        this.editor?.quillEditor?.insertEmbed(selectionIndex, 'image', imageUrl);

        if (this.editor?.quillEditor?.root?.innerHTML) {
          this.control?.setValue(this.editor.quillEditor.root.innerHTML);
          this.control?.updateValueAndValidity();
          this.editor?.quillEditor?.setSelection(selectionIndex + 1, 0);
        }
      });
  }

  #mentionListRows(): WysiwygMentionItem[] {
    if (this.mentionItems.length > 0) {
      return this.mentionItems;
    }
    return this.mentions.map(str => ({ id: str, value: str }));
  }

  #mentionListHasDescriptions(): boolean {
    return this.#mentionListRows().some(r => !!r.description?.trim());
  }

  #renderMentionListItem(item: WysiwygMentionItem, _searchTerm: string): HTMLElement | string {
    const description = item.description?.trim();
    if (!description) {
      return item.value;
    }
    const wrap = document.createElement('div');
    wrap.className = 'ui-wysiwyg-mention-item';
    const title = document.createElement('span');
    title.className = 'ui-wysiwyg-mention-item__value';
    title.setAttribute('data-denotation-char', this.#mentionDenotationChar);
    title.textContent = item.value;
    wrap.appendChild(title);
    const sub = document.createElement('span');
    sub.className = 'ui-wysiwyg-mention-item__description';
    sub.textContent = description;
    wrap.appendChild(sub);
    return wrap;
  }

  #quillMentionConfig(): QuillModules {
    const richMentionRows = this.mentionItems.length > 0 || this.#mentionListHasDescriptions();
    return {
      toolbar: {
        container: this.#setupToolbar(),
        handlers: {
          library: () => {
            this.#openMediaLibrary();
          },
          mention: () => {
            (this.editor?.quillEditor?.getModule('mention') as Mention).openMenu(this.#mentionDenotationChar);
          },
        },
      },
      mention: {
        allowedChars: /^[A-Za-z\s]*$/,
        mentionDenotationChars: [this.#mentionDenotationChar],
        // showDenotationChar: false,
        dataAttributes: ['value'],
        ...(richMentionRows
          ? {
              renderItem: (item: WysiwygMentionItem, searchTerm: string) =>
                this.#renderMentionListItem(item, searchTerm),
            }
          : {}),
        onSelect: (item: any, insertItem: any) => {
          insertItem(item);
          const editor = this.editor?.quillEditor;
          editor?.insertText(editor?.getLength() - 1, ' ', 'user');
        },
        source: (searchTerm: any, renderList: any) => {
          const f = this.#mentionListRows();
          const term = (searchTerm as string).toLocaleLowerCase();
          if (searchTerm.length === 0) {
            renderList(f);
          } else {
            const filtered = f.filter(row => {
              const inValue = row.value.toLocaleLowerCase().indexOf(term) > -1;
              const inDesc = (row.description ?? '').toLocaleLowerCase().indexOf(term) > -1;
              return inValue || inDesc;
            });
            renderList(filtered, searchTerm);
          }
        },
      },
    };
  }

  #setupToolbar() {
    let toolbar: string[][] | string[];
    if (this.toolbar.length > 0) {
      toolbar = this.toolbar;
    } else if (this.mode === 'variables') {
      toolbar = ['mention'];
    } else if (this.mode === 'plain') {
      toolbar = [];
    } else if (this.mode === 'whatsApp') {
      toolbar = ['bold', 'underline'];
    } else {
      toolbar = [
        ['bold', 'underline'],
        ['library', 'link'],
      ];
    }
    if ((this.mentions.length > 0 || this.mentionItems.length > 0) && this.mode !== 'variables') {
      const target = (isArray(toolbar[0]) ? toolbar[0] : toolbar) as string[];
      target.push('mention');
    }
    return toolbar;
  }

  #removeInlineImagesFromContent() {
    setTimeout(() => {
      if (!this.editor) {
        return;
      }

      const quillEditor = this.editor.quillEditor;
      const contents = quillEditor.getContents();

      if (contents.ops) {
        const filteredOps = contents.ops.filter(
          (op: Op) => !(typeof op.insert === 'object' && (op.insert['image'] as string)?.startsWith('data:image/')),
        );

        if (filteredOps.length !== contents.ops.length) {
          quillEditor.setContents(filteredOps);
          this.toastrService.show(this.translateService.instant('SH.USE_MEDIA_LIBRARY_TO_ADD_IMAGE'));
        }
      }
    }, 200);
  }
}
